import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Loader2, CheckCircle2, HeartPulse, Download, FileUp } from "lucide-react";

const emptyForm = {
  name: "",
  birth_date: "",
  nik: "",
  gender: "L",
  phone: "",
  desa: "",
  rt: "",
  rw: "",
  kecamatan: "Dawe",
  kabupaten: "Kudus",
};

const CONTOH_URL = `${process.env.REACT_APP_BACKEND_URL}/api/public/kartu-sehat/contoh-rekomendasi`;

function FileField({ label, hint, testid, file, onChange }) {
  return (
    <div className="space-y-2">
      <Label>{label} *</Label>
      <label className="flex items-center gap-2 rounded-md border border-dashed border-slate-300 px-3 py-2.5 text-sm cursor-pointer hover:border-emerald-400 hover:bg-emerald-50/50 transition-colors">
        <FileUp className="h-4 w-4 text-emerald-700 shrink-0" />
        <span className="truncate text-slate-600" data-testid={`${testid}-name`}>{file ? file.name : hint}</span>
        <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" data-testid={testid} onChange={(e) => onChange(e.target.files?.[0] || null)} />
      </label>
    </div>
  );
}

export default function KartuSehat() {
  const [form, setForm] = useState(emptyForm);
  const [rekomendasi, setRekomendasi] = useState(null);
  const [ktp, setKtp] = useState(null);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Nama lengkap dan No. WA wajib diisi");
      return;
    }
    if (!rekomendasi || !ktp) {
      toast.error("Surat Rekomendasi dan Foto KTP wajib diunggah");
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("rekomendasi", rekomendasi);
      fd.append("ktp", ktp);
      const { data } = await api.post("/public/kartu-sehat", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Pendaftaran Kartu Sehat Gratis berhasil dikirim");
      const sent = (data.wa_notifs || []).filter((w) => w.ok).length;
      if (sent > 0) toast.success("Notifikasi WA terkirim ke nomor Anda");
      setSuccess(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="kartu-sehat-page">
      <header className="border-b border-slate-200 bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-3">
          <Link to="/"><img src="/logo-lazisnu.png" alt="Logo LAZISNU Dawe" className="h-10 w-10 rounded-lg bg-white object-contain p-0.5" /></Link>
          <div className="flex-1">
            <p className="font-heading font-bold text-slate-900 leading-tight">Kartu Sehat Gratis</p>
            <p className="text-xs text-slate-500">Klinik Pratama NU Medika Dawe × NU Care-LAZISNU Dawe</p>
          </div>
          <Link to="/">
            <Button variant="outline" size="sm" data-testid="kartu-sehat-back-button">
              <ArrowLeft className="mr-1.5 h-4 w-4" /> Kembali
            </Button>
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-8 space-y-6">
        <img src="/banner-kartu-sehat.webp" alt="Banner Program Kartu Sehat Gratis" className="w-full rounded-2xl shadow-sm border border-slate-200" data-testid="kartu-sehat-banner" />

        {success ? (
          <Card data-testid="kartu-sehat-success">
            <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
              <CheckCircle2 className="h-14 w-14 text-emerald-700" />
              <p className="font-heading text-xl font-bold text-slate-800">Pendaftaran Terkirim</p>
              <p className="text-sm text-slate-500 max-w-md">
                Data pendaftaran Kartu Sehat Gratis Anda sudah kami terima. Selanjutnya akan dilakukan asesmen oleh petugas kami. Kartu digital akan dikirim ke No. WA Anda setelah disetujui. Terima kasih.
              </p>
              <Link to="/">
                <Button className="mt-2 bg-emerald-800 hover:bg-emerald-700 text-white" data-testid="kartu-sehat-success-back">Kembali ke Beranda</Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <Card data-testid="kartu-sehat-form-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <HeartPulse className="h-5 w-5 text-emerald-800" />
                Form Pendaftaran Kartu Sehat Gratis
              </CardTitle>
              <p className="text-xs text-slate-500">Isi data di bawah ini tanpa perlu login — No. Rekam Medis (RM) akan diisi oleh petugas setelah asesmen</p>
              <a href={CONTOH_URL} data-testid="kartu-sehat-unduh-contoh" className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 hover:text-emerald-800 hover:underline w-fit">
                <Download className="h-3.5 w-3.5" /> Unduh Contoh Surat Rekomendasi
              </a>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="kartu-sehat-form">
                <div className="space-y-2">
                  <Label>Nama Lengkap *</Label>
                  <Input data-testid="kartu-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
                </div>
                <div className="space-y-2">
                  <Label>Tanggal Lahir</Label>
                  <Input type="date" data-testid="kartu-birth-date-input" value={form.birth_date} onChange={(e) => setForm({ ...form, birth_date: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>No. KTP / NIK</Label>
                  <Input data-testid="kartu-nik-input" value={form.nik} onChange={(e) => setForm({ ...form, nik: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Jenis Kelamin</Label>
                  <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                    <SelectTrigger data-testid="kartu-gender-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="L" data-testid="kartu-gender-l">Laki-laki</SelectItem>
                      <SelectItem value="P" data-testid="kartu-gender-p">Perempuan</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>No. WA *</Label>
                  <Input data-testid="kartu-phone-input" placeholder="08xxxxxxxxxx" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required />
                </div>
                <div className="space-y-2">
                  <Label>Desa</Label>
                  <Input data-testid="kartu-desa-input" value={form.desa} onChange={(e) => setForm({ ...form, desa: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>RT</Label>
                  <Input data-testid="kartu-rt-input" value={form.rt} onChange={(e) => setForm({ ...form, rt: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>RW</Label>
                  <Input data-testid="kartu-rw-input" value={form.rw} onChange={(e) => setForm({ ...form, rw: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Kecamatan</Label>
                  <Input data-testid="kartu-kecamatan-input" value={form.kecamatan} onChange={(e) => setForm({ ...form, kecamatan: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Kabupaten</Label>
                  <Input data-testid="kartu-kabupaten-input" value={form.kabupaten} onChange={(e) => setForm({ ...form, kabupaten: e.target.value })} />
                </div>
                <FileField label="Unggah Surat Rekomendasi" hint="Pilih file (PDF/JPG/PNG)" testid="kartu-rekomendasi-input" file={rekomendasi} onChange={setRekomendasi} />
                <FileField label="Unggah Foto KTP" hint="Pilih file (PDF/JPG/PNG)" testid="kartu-ktp-input" file={ktp} onChange={setKtp} />
                <div className="md:col-span-2">
                  <Button type="submit" data-testid="kartu-submit-button" disabled={saving} className="w-full bg-emerald-800 hover:bg-emerald-700 text-white">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Kirim Pendaftaran"}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
